import * as React from 'react';
import PropTypes from 'prop-types';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import CssBaseline from '@mui/material/CssBaseline';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Box from '@mui/material/Box';
import { Container, Link, IconButton, Drawer, List, ListItem, ListItemText } from '@mui/material';
import Button from '@mui/material/Button';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTheme, useMediaQuery } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

function ElevationScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window() : undefined,
  });

  return children ? React.cloneElement(children, {
    elevation: trigger ? 4 : 0,
  }) : null;
}

ElevationScroll.propTypes = {
  children: PropTypes.element,
  window: PropTypes.func,
};

export default function Header(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState(null);
  const location = useLocation();
  const currentPath = `${location.pathname}${location.hash}`;

  const isLoginPage = location.pathname === '/login';
  const navigate = useNavigate();

  const handleNavClick = (item) => {
    if (item.href?.startsWith('/#')) {
      const hash = item.href.replace('/#', '');

      if (location.pathname !== '/') {
        navigate(`/#${hash}`);
      } else {
        navigate(`#${hash}`);
      }

    } else {
      navigate(item.href);
    }
    setDrawerOpen(false);
  };

  React.useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const id = location.hash.replace('#', '');

      const scrollToSection = () => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          requestAnimationFrame(scrollToSection);
        }
      };

      scrollToSection();
    }
  }, [location.pathname, location.hash]);

  React.useEffect(() => {
    const sections = ['features', 'methodology', 'tools', 'roi', 'testimonials', 'demo'];
    let ticking = false;

    const updateActiveSection = () => {
      let current = '';
      const scrollY = window.scrollY + 100;

      sections.forEach(section => {
        const el = document.getElementById(section);
        if (el && el.offsetTop <= scrollY) {
          current = section;
        }
      });

      if (current && current !== activeSection) {
        setActiveSection(current);
        window.history.replaceState(null, '', `/#${current}`);
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateActiveSection);
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  const toggleDrawer = (open) => (event) => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const menuItems = [
    { text: 'Features', href: '/#features', id: 'features' },
    { text: '8D Methodology', href: '/#methodology', id: 'methodology' },
    { text: 'Analysis Tools', href: '/#tools', id: 'tools' },
    { text: 'ROI Calculator', href: '/#roi', id: 'roi' },
    { text: 'Testimonials', href: '/#testimonials', id: 'testimonials' },
    { text: 'Demo', href: '/#demo', id: 'demo' },
    { text: 'Article', href: '/article' },
  ];

  const getActiveItem = () => {
    const urlActive = menuItems.find(item => {
      if (item.href.startsWith('/#')) {
        return currentPath === item.href;
      }
      return location.pathname === item.href;
    })?.text;

    return activeSection ? menuItems.find(item => item.id === activeSection)?.text : urlActive;
  };

  const activeItem = getActiveItem();

  const NavLink = ({ item }) => (
    <Link
      component="button"
      onClick={() => handleNavClick(item)}
      underline="none"
      sx={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: (item.text === activeItem || item.id === activeSection)
          ? '#1E5BB8'
          : '#5a5959ff',
        fontWeight: (item.text === activeItem || item.id === activeSection) ? 700 : 500,
      }}
    >
      {item.text}
    </Link>
  );

  const loginButton = (
    <Button
      variant="contained"
      onClick={() => navigate('/login')}
      sx={{
        ml: 2,
        px: 3,
        py: 1.25,
        borderRadius: '8px',
        fontWeight: 600,
        color: 'white',
        textTransform: 'none',
        background: 'linear-gradient(90deg, #1E5BB8 0%, #0047AB 100%)',
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
          opacity: 0.9,
        },
      }}
    >
      Login
    </Button>
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <ElevationScroll {...props}>
        <AppBar
          sx={{
            backgroundColor: 'white',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
            zIndex: 50,
          }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
            <Toolbar disableGutters sx={{ height: '80px', minHeight: '80px', justifyContent: 'space-between' }}>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                <Link href="/" underline="none" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  <img
                    src={'/images/gbi_.png'}
                    alt="Logo"
                    style={{ height: 50, flexShrink: 0 }}
                  />
                  <Box sx={{ height: 50, width: '1px', backgroundColor: '#c0bcbcff', flexShrink: 0 }} />

                  <Box sx={{ minWidth: 0, maxWidth: { xs: '150px', sm: '200px', md: 'none' } }}>
                    <Box component="h1" sx={{
                      fontSize: '1.5rem',
                      fontWeight: 700,
                      color: '#1E5BB8',
                      m: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      NC Pro
                    </Box>
                    <Box component="p" sx={{
                      fontSize: { xs: '0.65rem', sm: '0.75rem' },
                      color: '#777575ff',
                      m: 0,
                      lineHeight: 1.2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>
                      AI-Powered Quality Management
                    </Box>
                  </Box>
                </Link>
              </Box>

              {isLoginPage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mr: 5 }}>
                  <Button
                    onClick={() => navigate('/article')}
                    sx={{
                      ml: 4,
                      py: 1.25,
                      color: '#5a5959ff',
                      borderRadius: '8px',
                      fontWeight: 500,
                      fontSize: '1rem',
                      textTransform: 'none',
                      '&:hover': {
                        color: '#0047AB'
                      }
                    }}
                  >
                    Article
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    sx={{
                      ml: 2,
                      py: 1.25,
                      color: '#5a5959ff',
                      borderRadius: '8px',
                      fontWeight: 500,
                      fontSize: '1rem',
                      textTransform: 'none',
                      '&:hover': {
                        color: '#0047AB'
                      }
                    }}
                  >
                    Home
                  </Button>
                </Box>
              )}

              {!isMobile && !isLoginPage && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  {menuItems.map((item) => (
                    <NavLink key={item.text} item={item} />
                  ))}
                  {loginButton}
                </Box>
              )}

              {isMobile && !isLoginPage && (
                <IconButton
                  edge="end"
                  aria-label="menu"
                  onClick={toggleDrawer(true)}
                  sx={{
                    color: 'gray.700',
                    p: 1,
                  }}
                >
                  {drawerOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              )}
            </Toolbar>
          </Container>
        </AppBar>
      </ElevationScroll>

      <Drawer
        anchor="top"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: { width: '100%', maxHeight: '85vh' }
        }}
      >
        <Box
          sx={{
            width: '100%',
            py: 2,
          }}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 2 }}>
            <IconButton onClick={toggleDrawer(false)} color="inherit">
              <CloseIcon sx={{ color: '#000000' }} />
            </IconButton>
          </Box>

          <List sx={{ pt: 0 }}>
            {menuItems.map((item) => (
              <ListItem
                button
                key={item.text}
                onClick={() => handleNavClick(item)}
                sx={{
                  py: 1.5,
                  backgroundColor: item.id === activeSection ? 'rgba(30, 91, 184, 0.08)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(30, 91, 184, 0.08)',
                  }
                }}
              >
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiTypography-root': {
                      color: item.id === activeSection ? '#1E5BB8' : '#808080',
                      fontWeight: item.id === activeSection ? 700 : 700,
                      transition: 'color 0.3s',
                    },
                  }}
                />
              </ListItem>
            ))}
            {!isLoginPage && (
              <Box sx={{ px: 2, pt: 2, pb: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  href="/login"
                  sx={{
                    px: 3,
                    py: 1.25,
                    borderRadius: '8px',
                    fontWeight: 600,
                    color: 'white',
                    textTransform: 'none',
                    background: 'linear-gradient(90deg, #1E5BB8 0%, #0047AB 100%)',
                    boxShadow: 'none',
                    '&:hover': {
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
                      opacity: 0.9,
                    },
                  }}
                >
                  Login
                </Button>
              </Box>
            )}
          </List>
        </Box>
      </Drawer>

      <Toolbar />
    </React.Fragment>
  );
}